/**
 * Voices validation errors rendered by resources/views/components/input-error.blade.php
 * (`announce` prop) through the layout's <x-live-announcer /> regions, and one-shot status
 * messages that a page raises with the `blink:announce` window event (see announce()).
 *
 * This lives in JS rather than in the Blade template because Livewire morphs error
 * markup in place. The component used to write the message from an Alpine `x-init`,
 * which only runs when Alpine first initialises a node — so a second failed save
 * carrying the same error re-rendered an identical node, ran nothing, and left the
 * region untouched. Every failure after the first was silent.
 *
 * Design rules, each bought with a reproduced failure:
 *
 *  - The message arrives as an HTML data attribute, never as part of a JS expression.
 *    Blade escapes for HTML, which is the right escaping for that position; building a
 *    JS string literal out of it instead meant an apostrophe in a message (or in a
 *    customer-authored HubSpot property label) produced a SyntaxError and killed the
 *    announcement outright.
 *
 *  - Announcements go to the app-level announcer, not a per-field region. A live
 *    region inside Livewire-morphed markup is not reliably voiced: after an error was
 *    fixed and then retriggered, VoiceOver stayed silent even though the region's DOM
 *    mutated correctly. The announcer exists from first paint and no morph touches it.
 *
 *  - The two announcer regions alternate, so every announcement is a clean
 *    empty-to-text change in a long-lived region — a repeated identical error is
 *    still a change, which same-region rewrites are not guaranteed to be.
 *
 *  - A message is re-voiced only on renders that ran a component method. Livewire's
 *    error bag survives until validation runs again, so fixing a field first produces
 *    a property-sync render that still carries the old error; announcing on that one
 *    talked over the user as they corrected it. A property sync carries no calls, a
 *    button press does, which separates "you failed again" from "nothing has been
 *    re-checked yet".
 *
 *  - A render that changed some error to a message announces only those changes;
 *    otherwise — an exact resubmit, or a fix that resolved one error while others
 *    still block the save — the still-standing set is read, because silence after
 *    a rejected save reads as success (fix a key form's expiry while its name is
 *    still missing, and a silent Create Key means no key and no explanation). A
 *    fix is silent only when it leaves nothing standing. And the region
 *    self-empties shortly after speaking, so a browse cursor doesn't stumble over
 *    a long-gone error later.
 */

const SOURCE_SELECTOR = '[data-announce-field]';

/** Collects the fields of one server round-trip (several morphs) into one announcement. */
const FLUSH_DELAY_MS = 120;

/**
 * An announcement is a one-shot event, but the region's text would otherwise persist —
 * and a screen reader's browse cursor reads whatever it lands on, so a user arrowing
 * through the page would stumble over a long-gone error. Emptying the region does not
 * announce (removals are not in aria-relevant's default), so this is silent cleanup.
 */
const CLEAR_AFTER_MS = 7000;

/*
 * All of this state is module-global, shared by every Livewire component on the
 * page, and lastMessageByField is keyed by field name alone. Safe today because
 * each announce-bearing form sits alone on its page — but two components sharing a
 * field name (say, two "operation" selects) on one page would cross their change
 * tracking. If that page ever exists, key by component id + field name.
 */
const lastMessageByField = new Map();

/** Component ids whose next morph followed a method call, so may repeat an unchanged message. */
const componentsAwaitingAnnounce = new Set();

/** message -> 'changed' | 'standing' for the current flush cycle; 'changed' wins. */
const queuedMessages = new Map();

let flushTimer = null;

let clearTimer = null;

let useRegionB = false;

function flushQueue() {
    flushTimer = null;

    const regionA = document.getElementById('live-announcer-a');
    const regionB = document.getElementById('live-announcer-b');

    /*
     * A render's news is the errors it changed, so when a change produced a message,
     * only those are read — re-reading every still-standing error over the user was
     * the noisiest defect screen-reader testing found. (Deliberate trade-off: a
     * changed message also mutes other standing ones; they were announced when they
     * appeared and are still on screen.) But when the cycle changed nothing to a
     * message — an exact resubmit, or a fix that resolved one error while others
     * still block the save — the standing set is read instead: those errors are why
     * the save was rejected, and silence there reads as success.
     */
    const entries = Array.from(queuedMessages.entries());
    const changed = entries.filter(([, kind]) => kind === 'changed').map(([message]) => message);
    const message = (changed.length > 0 ? changed : entries.map(([m]) => m)).join(' ');

    queuedMessages.clear();

    if (message === '' || !regionA || !regionB) {
        return;
    }

    useRegionB = !useRegionB;

    const target = useRegionB ? regionB : regionA;
    const other = useRegionB ? regionA : regionB;

    other.textContent = '';
    target.textContent = message;

    if (clearTimer) {
        clearTimeout(clearTimer);
    }

    clearTimer = setTimeout(() => {
        clearTimer = null;
        target.textContent = '';
    }, CLEAR_AFTER_MS);
}

function considerSource(source, mayRepeat) {
    const field = source.dataset.announceField;
    const message = source.dataset.announceMessage || '';
    const previous = lastMessageByField.get(field) || '';

    lastMessageByField.set(field, message);

    if (message === '' || (message === previous && !mayRepeat)) {
        return;
    }

    const kind = message === previous ? 'standing' : 'changed';

    if (queuedMessages.get(message) !== 'changed') {
        queuedMessages.set(message, kind);
    }

    if (flushTimer) {
        clearTimeout(flushTimer);
    }

    flushTimer = setTimeout(flushQueue, FLUSH_DELAY_MS);
}

function syncAnnouncedErrors(root, mayRepeat) {
    const scope = root && typeof root.querySelectorAll === 'function' ? root : document;

    if (scope !== document && typeof scope.matches === 'function' && scope.matches(SOURCE_SELECTOR)) {
        considerSource(scope, mayRepeat);
    }

    scope.querySelectorAll(SOURCE_SELECTOR).forEach((source) => considerSource(source, mayRepeat));
}

/** Errors present in the first server render (a plain form post, or a Livewire first paint). */
document.addEventListener('DOMContentLoaded', () => syncAnnouncedErrors(document, true));

document.addEventListener('livewire:navigated', () => syncAnnouncedErrors(document, true));

document.addEventListener('livewire:init', () => {
    window.Livewire.hook('commit', ({ component, commit }) => {
        if (component && Array.isArray(commit.calls) && commit.calls.length > 0) {
            componentsAwaitingAnnounce.add(component.id);
        }
    });

    window.Livewire.hook('morphed', ({ el, component }) => {
        const id = component && component.id;
        const mayRepeat = componentsAwaitingAnnounce.delete(id);

        syncAnnouncedErrors(el, mayRepeat);
    });
});

/**
 * A one-shot status message through the same regions, for a result nothing on the page voices
 * by itself: a team created from the invite form (whose dialog closes onto a re-rendered form),
 * an invitation edit saved. It joins the current flush cycle as a change, so it is read alongside
 * any error the same round-trip produced, alternates regions and self-empties like an error.
 *
 * Raised from Blade with Alpine's `$dispatch('blink:announce', text)`, which bubbles up to the
 * document this module already listens on (the Node harness in SiteAnalyticsRetryComponentTest
 * stubs `document`, not `window`). The text is set as textContent, never parsed as HTML. A
 * page-local role="status" region would not do: one rendered by Livewire morphs with the page
 * (the failure the file comment describes), and one kept out of the morph would be a second
 * announcer to keep in step with this one.
 */
export function announce(message) {
    if (typeof message !== 'string' || message === '') {
        return;
    }

    queuedMessages.set(message, 'changed');

    if (flushTimer) {
        clearTimeout(flushTimer);
    }

    flushTimer = setTimeout(flushQueue, FLUSH_DELAY_MS);
}

document.addEventListener('blink:announce', (event) => announce(event.detail));

export default syncAnnouncedErrors;
